import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { pricingFeatures, pricingPlans, PricingFeature, getFeatureValue } from '@/data/pricingPlans';

interface FeatureComparisonProps {
  className?: string;
}

const FeatureIcon: React.FC<{ included: boolean | string }> = ({ included }) => {
  if (typeof included === 'string') {
    return <span className="text-sm text-gray-700 font-medium">{included}</span>;
  }
  return included ? (
    <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
  ) : (
    <X className="h-5 w-5 text-gray-300 mx-auto" />
  );
};

export const FeatureComparison: React.FC<FeatureComparisonProps> = ({ className = '' }) => {
  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Feature Comparison
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See exactly what's included in each plan to make the best choice for your program.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-4 font-semibold text-gray-800">Features</th>
                      <th className="text-center p-4 font-semibold text-gray-800">
                        Essential
                        <div className="text-sm font-normal text-gray-600 mt-1">$49/month</div>
                      </th>
                      <th className="text-center p-4 font-semibold text-gray-800">
                        Advanced
                        <div className="text-sm font-normal text-gray-600 mt-1">$99/month</div>
                        <div className="text-xs text-[#FA8C00] font-medium mt-1">Most Popular</div>
                      </th>
                      <th className="text-center p-4 font-semibold text-gray-800">
                        Enterprise
                        <div className="text-sm font-normal text-gray-600 mt-1">$199/month</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingFeatures.map((feature, index) => (
                      <tr key={index} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="font-medium text-gray-900">{feature.name}</div>
                          {feature.description && (
                            <div className="text-sm text-gray-600 mt-1">{feature.description}</div>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <FeatureIcon included={feature.essential} />
                        </td>
                        <td className="p-4 text-center">
                          <FeatureIcon included={feature.advanced} />
                        </td>
                        <td className="p-4 text-center">
                          <FeatureIcon included={feature.enterprise} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-6 p-4">
                {pricingPlans.map((plan, planIndex) => {
                  const planFeatures = pricingFeatures.filter(feature => {
                    const featureValue = getFeatureValue(feature, plan.id);
                    return featureValue !== false;
                  });

                  const isPopular = plan.popular;

                  return (
                    <Card key={planIndex} className={`${isPopular ? 'border-[#FA8C00] border-2' : 'border-gray-200'}`}>
                      <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-bold text-gray-800 flex items-center justify-between">
                          <span>{plan.name}</span>
                          <span className="text-2xl">${plan.price}/month</span>
                        </CardTitle>
                        {isPopular && (
                          <div className="text-sm text-[#FA8C00] font-medium">Most Popular</div>
                        )}
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          {planFeatures.map((feature, featureIndex) => {
                            const featureValue = getFeatureValue(feature, plan.id);
                            
                            return (
                              <div key={featureIndex} className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{feature.name}</div>
                                  {feature.description && (
                                    <div className="text-xs text-gray-600 mt-1">{feature.description}</div>
                                  )}
                                  {typeof featureValue === 'string' && (
                                    <div className="text-xs text-[#FA8C00] font-medium mt-1">{featureValue}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};